import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component } from '../../../concepts/objects/Component';
import { useWindowSizeInRem } from '../../../platform/Hook';
import { Float, remInPx, usePrompt } from '../../../ui';
import { FixedScroller } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { bringToFront, place } from '../../canvas/commands/OrderCommands';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { ComponentMount } from '../../canvas/visualization/ComponentMount';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { VisualConfiguration } from '../../canvas/visualization/VisualConfiguration';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { DropZone, DropZonePeekOpen } from './DropZone';
import { dropZoneUnpeek } from './DropZoneCommands';

export const InlineComponentMount = ({ object }: { object: Physical }) => {

    const ticker = useComponentObserver(object);

    if (!object.alive) return null;
    const Visual = VisualConfiguration[object.type];
    if (!Visual) return null;

    const { width, height } = useMemo(() => object.bounds(RelativeTo.Model), [ticker]);
    const transform = object.transform(RelativeTo.Model).inverse().toCSS();

    return <div style={{ position: 'relative', width, height, pointerEvents: 'none', userSelect: 'none' }} >
        <div style={{ transform }}>
            <Visual key={object.id} object={object}>
                {object instanceof Component && (
                    object.children.map((x: any) => <ComponentMount key={x.id} object={x} />)
                )}
            </Visual>
        </div>
    </div>;
};

const ComponentButton = styled.div`

    position: relative;
    cursor: pointer;

    &:hover {
        &:after {
            content: '';
            position: absolute;
            inset: -.5rem;
            background: rgb(98 155 213 / 10%);
            border-radius: 4px;
        }
    }

    &:active {
        &:after {
            content: '';
            position: absolute;
            inset: -.5rem;
            background: rgb(98 155 213 / 20%);
            border-radius: 4px;
        }
    }
`;

export const DropZonePeekWidget = ({ widget }: { widget: PopoutWidget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const subject = widget.subject as DropZone;
    const ws = useWindowSizeInRem();

    const ticker = useComponentObserver(subject);
    const items = useMemo(() => subject.polarize(AutoBringToFront).reverse(), [ticker]);
    const width = useMemo(() => Math.max(...items.map(x => x.size().w)) + remInPx(2), [items]);

    const onSelect = (item: Physical) => {
        dispatch(bringToFront, [item.id]);
        dispatch(place, [item.id]);
        dispatch(dropZoneUnpeek);
    };

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction() {
            dispatch(dropZoneUnpeek);
        }
    });

    useEffect(() => {
        DropZonePeekOpen.set(subject, true);
        return () => void (DropZonePeekOpen.set(subject, false));
    }, []);

    return <Flexer ref={promptRef} width={`${width}px`}>
        <Float shadow="xxl">
            <FixedScroller maxHeight={ws.height * .6}>
                <Flexer flex="column" padding="1" gap="1" align="center">
                    {items.map(x =>
                        <ComponentButton key={x.id} onClick={() => onSelect(x)}>
                            <InlineComponentMount object={x} />
                        </ComponentButton>
                    )}
                </Flexer>
            </FixedScroller>
        </Float>
    </Flexer>;
};
